import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { useI18n, useLicenses } from '~Hooks';
import TouchedWrapper from '~Scenes/SelfServiceKiosks/Components/TouchedWrapper';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 11;
  width: 100%;
  height: 100%;
  padding: 264px 120px 165px 120px;
  box-sizing: border-box;

  @media (orientation: landscape) {
    padding: 148px 67px 92px 67px;
    justify-content: center;
  }
`;

const Title = styled.h2`
  margin: 0 0 40px 0;
  color: rgb(19, 19, 22);
  font-family: Eina01-SemiBold;
  font-size: 80px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 96px;
  text-align: center;

  @media (orientation: landscape) {
    font-size: 68px;
    line-height: 61px;
  }
`;

const Description = styled.p`
  margin: 0;
  color: rgb(19, 19, 22);
  font-family: Eina01-Regular;
  font-size: 48px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 56px;
  text-align: center;

  @media (orientation: landscape) {
    font-size: 40px;
    line-height: 47px;
  }
`;

const Number = styled.div`
  margin: 148px 0 0 0;
  padding: 0 50px;
  min-width: 624px;
  height: 420px;
  line-height: 420px;
  background: rgb(245, 245, 248);
  color: rgb(19, 19, 22);
  font-family: Eina01-SemiBold;
  font-size: 180px;
  font-weight: 600;
  text-align: center;

  @media (orientation: landscape) {
    margin: 83px 0 0 0;
    min-width: 351px;
    height: 236px;
    line-height: 236px;
    font-size: 101px;
  }
`;

const NewOrderBtn = styled(TouchedWrapper)`
  font-family: Eina01-SemiBold;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 20px;
  border: 1px solid #e0e3e3;
  border-radius: 32px;
  padding: 36px 52px;
  margin-top: 72px;
  text-align: center;
  color: rgb(33, 33, 36);
  background: rgb(255, 255, 255);
  width: 344px;
`;

type Props = {
  orderNumber: string | undefined | null;
  done: () => void;
  showOrderNumber: boolean;
};

const OrderDisplay: React.FunctionComponent<Props> = ({
  orderNumber,
  done,
  showOrderNumber,
}: Props) => {
  const { i18n } = useI18n();
  const licenses = useLicenses();
  const [count, setCount] = useState(0);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      done();
    }, 11000);

    if (!orderNumber || !showOrderNumber) {
      return;
    }
    counter(0, parseInt(orderNumber));

    return () => {
      clearTimeout(timeoutId);
    };
  }, [showOrderNumber, orderNumber]);

  const counter = (min: number, max: number) => {
    for (let count = min; count <= max; count++) {
      setTimeout(() => {
        setCount(count);
      }, 2000);
    }
  };

  return (
    <Wrapper>
      <Title>{i18n('SSK.OrderNumber.Title')}!</Title>
      <Description>
        {licenses.atleastOneDeviceHasCustomerDisplay
          ? i18n('SSK.OrderNumber.SubTitle.OrderDisplay')
          : i18n('SSK.OrderNumber.Subtitle.Generic')}
      </Description>
      <Number>{count}</Number>
      <NewOrderBtn
        onClick={done}
        shadows={{
          pressed: '0px 1px 18px 0px rgba(0, 0, 0, 0.12)',
          init:
            '0px 3px 5px -1px rgb(0 0 0 / 20%), 0px 6px 10px 0px rgb(0 0 0 / 14%), 0px 1px 18px 3px rgb(0 0 0 / 12%)',
        }}>
        {i18n('Express.OrderNumber.NewOrder')}
      </NewOrderBtn>
    </Wrapper>
  );
};

export default OrderDisplay;
